import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./api-client";
import useCommonStore from "../store/CommonStore";

// interface User {
//   id: number;
//   name: string;
// }

interface DashboardQuery {
  startDate?: string;
  endDate?: string;
  isAllData?: boolean;
}

interface User {
  user_id: string;
  username: string;
  user_email: string;
  country: string;
  total_prompt: number;
  status: string;
  eng_score: number;
  created_at: string;
}
// interface UserList {
//   items: User[];
//   total: number;
//   page: number;
//   page_size: number;
//   total_pages: number;
//   has_next: boolean;
//   has_prev: boolean;
// }

interface Chat {
  id: number;
  message: string;
}

// interface ChatList {
//   items: Chat[];
//   total: number;
//   page: number;
//   page_size: number;
//   total_pages: number;
//   has_next: boolean;
//   has_prev: boolean;
// }

export interface Session {
  // id: number;
  total_active_sessions: number;
  users: any;
}

interface Project {
  id: number;
  name: string;
}

interface Research {
  id: number;
  title: string;
}

type Stats = {
  agents_usage: Record<string, number>;
  total_boards: number;
  total_chats: number;
  total_feedback: number;
  total_research: number;
  total_sessions: number;
  total_chats_rolesgpt: number;
  total_chats_hr: number;
  average_session_time: any;
};

type UserDetail = {
  app_id: string[];
  created_at: string;
  email: string;
  id: string;
  is_active: boolean;
  job_name: string | null;
  organization_name: string | null;
  role: string[];
  selected_llm: string;
  updated_at: string;
  username: string;
};

type UserResponse = {
  stats: Stats;
  user: UserDetail;
};

interface AdminStatus {
  total_users: number;
  total_chats: number;
  total_projects: number;
  // total_researches: number;
  active_user: number;
  active_chats: number;
  active_session: number;
  total_feedback: number;
  inactive_user: number;
}

export interface ChatDetails {
  chatListName: string;
  username: string;
  timestamp: string;
  agent: string;
  prompts: {
    id?: string;
    prompt_message_id: string;
    filename?: {
      contenttype: string;
      fileurl: string;
    }[];
    kpis?: string;
    prompt: string;
    database: string;
  }[];
  messages: {
    prompt_message_id: string;
    generatedText: string;
    crew_event: any[];
  }[];
}

export type FeedbackDetails = {
  id: string;
  app_name: string;
  llm: string;
  message_id: string;
  message_text: string;
  prompt_text: string;
  reaction?: string | null;
  selected_feedback?: string[];
  created_at: string;
  updated_at?: string;
  username?: string;
};

interface PatchChatPinPayload {
  action?: string;
}

export interface ApiResponse {
  items: any[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
  Sessions?: any;
}

function getPatchChatPinPayload(data: PatchChatPinPayload) {
  return {
    action: data.action,
  };
}

export function useUsers() {
  return useQuery<any, Error>({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await apiClient.get<any>("/v3/users");
      return response;
    },
  });
}

// export function useUsersPage(
//   page?: number,
//   pageSize?: number,
//   startDate?: string,
//   endDate?: string,
//   filteredStatus?: string,
//   isAllData?: boolean
// ) {
//   const isNewUser = filteredStatus === "new";

//   return useQuery<ApiResponse, Error>({
//     queryKey: ["users", page, pageSize, startDate, endDate, filteredStatus],
//     queryFn: async () => {
//       const params = new URLSearchParams({
//         page: String(page || 1),
//         page_size: String(pageSize || 10),
//       });

//       if (startDate) {
//         params.append("start_date", startDate);
//       }

//       if (endDate) {
//         params.append("end_date", endDate);
//       }

//       if (filteredStatus) {
//         if (isNewUser) {
//           params.append("new_user", "user");
//         } else {
//           params.append("status", filteredStatus);
//         }
//       }

//       const url = isAllData
//         ? `/v2/users?${params.toString()}?all=true`
//         : `/v2/users?${params.toString()}`;

//       const response = await apiClient.get<ApiResponse>(url);

//       return response;
//     },

//     // enabled: !!startDate && !!endDate,
//   });
// }

export function useUsersPage(
  page?: number,
  pageSize?: number,
  startDate?: string,
  endDate?: string,
  filteredStatus?: string,
  isAllData?: boolean
) {
  const isNewUser = filteredStatus === "new";

  return useQuery<ApiResponse, Error>({
    queryKey: [
      "users",
      page,
      pageSize,
      startDate,
      endDate,
      filteredStatus,
      isAllData,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page || 1),
        page_size: String(pageSize || 10),
      });

      if (!isAllData) {
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
      }

      if (!isAllData && filteredStatus) {
        if (isNewUser) {
          params.append("new_user", "user");
        } else {
          params.append("status", filteredStatus);
        }
      }

      if (isAllData) {
        params.append("all", "true");
      }

      const url = `/v2/users?${params.toString()}`;

      const response = await apiClient.get<ApiResponse>(url);
      return response;
    },
    // enabled: !!startDate && !!endDate, // (keep commented if not needed)
  });
}

export function useChats() {
  return useQuery<any, Error>({
    queryKey: ["chats"],
    queryFn: async () => {
      const response = await apiClient.get<any>("/Admin_chats");
      return response;
    },
    retryDelay: 300,
    // refetchInterval: 6000,
  });
}

// export function useChatsPage(
//   page?: number,
//   pageSize?: number,
//   startDate?: string,
//   endDate?: string,
//   appName?: string,
//   agentName?: string,
//   isAllData?: boolean,
//   userId?: string,
//   isActive?: boolean
// ) {
//   return useQuery<ApiResponse, Error>({
//     queryKey: [
//       "chats",
//       page,
//       pageSize,
//       startDate,
//       endDate,
//       appName,
//       agentName,
//       isAllData,
//       isActive,
//     ],
//     queryFn: async () => {
//       const params = new URLSearchParams({
//         page: String(page || 1),
//         page_size: String(pageSize || 10),
//       });

//       if (startDate) {
//         params.append("start_date", startDate);
//       }

//       if (endDate) {
//         params.append("end_date", endDate);
//       }

//       const url = isAllData
//         ? `/Admin_chats?skip=0&${params}&app_name=${appName}&agent=${agentName}&all=true`
//         : `/Admin_chats?${params}&app_name=${appName}&agent=${agentName}`;
//       // const response = await apiClient.get<ApiResponse>(
//       //   `/Admin_chats?${params}&app_name=${appName}&agent=${agentName}`
//       // );

//       const response = await apiClient.get<ApiResponse>(url);
//       return response;
//     },
//     // enabled: !!startDate && !!endDate,
//   });
// }

export function useChatsPage(
  page?: number,
  pageSize?: number,
  startDate?: string,
  endDate?: string,
  appName?: string,
  agentName?: string,
  isAllData?: boolean,
  selectedUser?: string,
  userId?: string,
  isActive: boolean = true
) {
  return useQuery<ApiResponse, Error>({
    queryKey: [
      "chats",
      {
        page,
        pageSize,
        startDate,
        endDate,
        appName,
        agentName,
        isAllData,
        selectedUser,
        userId,
      },
    ],
    // enabled: !!isActive,
    queryFn: async () => {
      const base = "/Admin_chats/";

      const params = new URLSearchParams();
      params.append("page", String(page ?? 1));
      params.append("page_size", String(pageSize ?? 10));

      if (!userId) {
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
      }

      if (appName) params.append("app_name", appName);
      if (agentName) params.append("agent", agentName);

      if (userId) {
        params.append("target_user_id", userId);
      }

      if (selectedUser) {
        params.append("target_user_id", selectedUser);
      }

      // const url = isAllData
      //   ? `/Admin_chats?skip=0&${params}&app_name=${appName}&agent=${agentName}&all=true`
      //   : `/Admin_chats?${params}&app_name=${appName}&agent=${agentName}`;
      // const response = await apiClient.get<ApiResponse>(
      //   `/Admin_chats?${params}&app_name=${appName}&agent=${agentName}`
      // );
      if (isAllData) params.append("all", "true");

      const url = `${base}?${params.toString()}`;
      const res = await apiClient.get<ApiResponse>(url);
      return res;
    },
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnReconnect: "always",
    refetchOnWindowFocus: false,
  });
}

export function useSessions() {
  return useQuery<any, Error>({
    queryKey: ["sessions"],
    queryFn: async () => {
      const response = await apiClient.get<any>("/v2/sessions");
      return response;
    },
    retryDelay: 300,
    // refetchInterval: 6000,
  });
}

export function useSessionsPage(
  page?: number,
  pageSize?: number,
  startDate?: string,
  endDate?: string,
  isAllData?: boolean,
  userId?: string,
  isActive?: boolean
) {
  return useQuery<ApiResponse, Error>({
    queryKey: ["sessions", page, pageSize, startDate, endDate, isAllData],
    queryFn: async () => {
      const base = "/v3/sessions/";
      const params = new URLSearchParams({
        page: String(page || 1),
        page_size: String(pageSize || 10),
      });

      if (!userId) {
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
      }

      if (userId) {
        params.append("target_user_id", userId);
      }

      if (isAllData) params.append("all", "true");

      const url = `${base}?${params.toString()}`;

      // const url = isAllData
      //   ? `/v3/sessions?all=true`
      //   : `/v3/sessions?${params}`;
      // const response = await apiClient.get<ApiResponse>(
      //   `/v3/sessions?${params}`
      // );

      const response = await apiClient.get<ApiResponse>(url);
      return response;
    },
  });
}

export function useUserLogs() {
  const { userLogPage, userLogPageSize } = useCommonStore();

  return useQuery<any, Error>({
    queryKey: ["user_logs"],
    queryFn: async () => {
      const response = await apiClient.get(
        `/user_logs?page=${userLogPage}&page_size=${userLogPageSize}`
      );
      return response;
    },
    retryDelay: 300,
    placeholderData: (prevData: any) => prevData ?? undefined,
  });
}

export function useProjects() {
  return useQuery<any, Error>({
    queryKey: ["projects"],
    queryFn: async () => {
      const response = await apiClient.get<any>("/Admin_boards");
      return response;
    },
  });
}

export function useProjectsPage(
  page?: number,
  pageSize?: number,
  startDate?: string,
  endDate?: string,
  isAllData?: boolean,
  userId?: string,
  isActive?: boolean
) {
  return useQuery<ApiResponse, Error>({
    queryKey: ["projects", page, pageSize, startDate, endDate, isAllData],
    queryFn: async () => {
      const base = "/Admin_boards/";
      const params = new URLSearchParams({
        page: String(page || 1),
        page_size: String(pageSize || 10),
      });

      // if (startDate) {
      //   params.append("start_date", startDate);
      // }

      // if (endDate) {
      //   params.append("end_date", endDate);
      // }

      // const url = isAllData
      //   ? `/Admin_boards?all=true`
      //   : `/Admin_boards?${params}`;

      if (!userId) {
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
      }

      if (userId) {
        params.append("target_user_id", userId);
      }

      if (isAllData) params.append("all", "true");

      const url = `${base}?${params.toString()}`;

      const response = await apiClient.get<ApiResponse>(url);
      return response;
    },
  });
}

export function useResearch() {
  return useQuery<any[], Error>({
    queryKey: ["research"],
    queryFn: async () => {
      const response = await apiClient.get<any[]>("/Admin_research");
      return response;
    },
  });
}

export function useFeedback() {
  return useQuery<any[], Error>({
    queryKey: ["feedback"],
    queryFn: async () => {
      const response = await apiClient.get<any[]>("/feedback");
      return response;
    },
  });
}

export function useFeedbackPage(
  page?: number,
  pageSize?: number,
  startDate?: string,
  endDate?: string,
  isAllData?: boolean,
  appName?: string,
  userId?: string,
  isActive?: boolean
) {
  return useQuery<ApiResponse, Error>({
    queryKey: [
      "feedback",
      page,
      pageSize,
      startDate,
      endDate,
      isAllData,
      appName,
    ],
    queryFn: async () => {
      const base = "/feedback/";

      const params = new URLSearchParams({
        page: String(page || 1),
        page_size: String(pageSize || 10),
      });

      // if (startDate) {
      //   params.append("start_date", startDate);
      // }

      // if (endDate) {
      //   params.append("end_date", endDate);
      // }

      // if (appName) {
      //   params.append("app_name", appName);
      // }

      // const url = isAllData
      //   ? `/feedback?all=true`
      //   : `/feedback?${params}&app_name=${appName}`;

      if (!userId) {
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
      }

      if (appName) params.append("app_name", appName);

      if (userId) {
        params.append("target_user_id", userId);
      }

      if (isAllData) params.append("all", "true");

      const url = `${base}?${params.toString()}`;
      const response = await apiClient.get<ApiResponse>(url);
      return response;
    },
  });
}

export function useRequestDemoPage(
  page?: number,
  pageSize?: number,
  startDate?: string,
  endDate?: string,
  status?: string
) {
  return useQuery<ApiResponse, Error>({
    queryKey: ["feedback", page, pageSize, startDate, endDate, status],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page || 1),
        page_size: String(pageSize || 10),
      });

      if (startDate) {
        params.append("start_date", startDate);
      }

      if (endDate) {
        params.append("end_date", endDate);
      }

      if (status) {
        params.append("status", status);
      }

      const response = await apiClient.get<ApiResponse>(
        `/demo-requests?${params}`
      );
      return response;
    },
  });
}

export function useMailUserList(
  page?: number,
  pageSize?: number,
  startDate?: string,
  endDate?: string,
  mailStatusFilter?: string
) {
  return useQuery<ApiResponse, Error>({
    queryKey: ["user-list", page, pageSize, startDate, endDate, mailStatusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page || 1),
        page_size: String(pageSize || 10),
      });

      if (startDate) {
        params.append("start_date", startDate);
      }

      if (endDate) {
        params.append("end_date", endDate);
      }

      if (mailStatusFilter) {
        params.append("mail_type", mailStatusFilter);
      }

      const response = await apiClient.get<ApiResponse>(
        `/mail-send-user?${params}`
      );
      return response;
    },
  });
}

// export function useDashboard() {
//   return useQuery<AdminStatus, Error>({
//     queryKey: ["admin-status"],
//     queryFn: async () => {
//       const response = await apiClient.get<AdminStatus>("/admin/stats");
//       return response;
//     },
//   });
// }

// export function useDashboard(startDate: string, endDate: string) {
//   return useQuery<AdminStatus, Error>({
//     queryKey: ["admin-status", startDate, endDate],
//     queryFn: async () => {
//       const url = `/admin/stats?start_date=${startDate}&end_date=${endDate}`;
//       const response = await apiClient.get<AdminStatus>(url);
//       return response;
//     },
//     // enabled: !!startDate && !!endDate,
//   });
// }

export function useDashboard({
  startDate,
  endDate,
  isAllData,
}: DashboardQuery) {
  return useQuery<AdminStatus, Error>({
    queryKey: ["admin-status", startDate, endDate, isAllData],
    queryFn: async () => {
      // const url = `/admin/stats?start_date=${startDate}&end_date=${endDate}`;
      // const url = `/admin/stats?start_date=${startDate}&end_date=${endDate}`;
      const url = isAllData
        ? `/admin/stats?all=true`
        : `/admin/stats?start_date=${startDate}&end_date=${endDate}`;
      const response = await apiClient.get<AdminStatus>(url);
      return response;
    },
    // enabled: !!startDate && !!endDate,
  });
}

export function useProjectByUserId(boardId: string) {
  return useQuery<Project, Error>({
    queryKey: ["board", boardId],
    queryFn: async () => {
      const response = await apiClient.get<Project>(`/user_boards/${boardId}`);
      return response;
    },
    enabled: !!boardId,
  });
}

export function useUserDetailsById(userId: string) {
  return useQuery<UserResponse, Error>({
    queryKey: ["user", userId],
    queryFn: async () => {
      const response = await apiClient.get<UserResponse>(
        `/admin/user_details/${userId}`
      );
      return response;
    },
    enabled: !!userId,
  });
}

export function useChatByUserId(chatId: string) {
  return useQuery<Chat, Error>({
    queryKey: ["chat", chatId],
    queryFn: async () => {
      const response = await apiClient.get<Chat>(`/user_chats/${chatId}`);
      return response;
    },
    enabled: !!chatId,
  });
}

export function useGetUserId(userId: any) {
  return useQuery<any, Error>({
    queryKey: ["userList", userId],
    queryFn: async () => {
      const response = await apiClient.get<any>(`/users/${userId}`);
      return response;
    },
    enabled: !!userId,
  });
}

export function usePatchEditUserStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { action: string; userId: string }) => {
      const payload = getPatchChatPinPayload(data);
      const userId = data.userId;

      return apiClient.patch(`/users/${userId}`, payload);
    },
    onSuccess: (data, variables) => {
      const { action, userId } = variables;

      const statusMapping: Record<string, string> = {
        activate: "Active",
        deactivate: "Deactive",
      };

      const updatedStatus = statusMapping[action.toLowerCase()] || action;

      queryClient.setQueryData<User[]>(["users"], (oldUsers = []) => {
        const updatedUsers = oldUsers.map((user) =>
          user.user_id === userId ? { ...user, status: updatedStatus } : user
        );

        return updatedUsers;
      });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => {
      return apiClient.deleteApi(`/users/${userId}`, {});
    },
    onSuccess: (_, userId) => {
      queryClient.setQueryData<User[]>(["users"], (oldUsers = []) =>
        oldUsers.filter((user) => user.user_id !== userId)
      );

      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useResearchByUserId(researchId: string) {
  return useQuery<Research, Error>({
    queryKey: ["research", researchId],
    queryFn: async () => {
      const response = await apiClient.get<Research>(
        `/user_research/${researchId}`
      );
      return response;
    },
    enabled: !!researchId,
  });
}

export function useChatDetails(chatId: string) {
  return useQuery<ChatDetails, Error>({
    queryKey: ["chatDetails", chatId],
    queryFn: async () => {
      const response = await apiClient.get<ChatDetails>(
        `/admin/chats/${chatId}`
      );
      return response;
    },
    enabled: !!chatId,
  });
}

export function useFeedbackDetails(feedbackId: string) {
  return useQuery<FeedbackDetails, Error>({
    queryKey: ["FeedbackDetails", feedbackId],
    queryFn: async () => {
      const response = await apiClient.get<FeedbackDetails>(
        `/feedback/${feedbackId}`
      );
      return response;
    },
    enabled: !!feedbackId,
  });
}

export function useAgentDetails(agentId: string) {
  return useQuery<ChatDetails, Error>({
    queryKey: ["agentDetails", agentId],
    queryFn: async () => {
      const response = await apiClient.get<ChatDetails>(
        `/admin/chats/${agentId}`
      );
      return response;
    },
    enabled: !!agentId,
  });
}

export async function signOut() {
  try {
    const response = await apiClient.post("/auth/logout", "");
    return response;
  } catch (error) {
    console.error("Logout failed:", error);
    throw error;
  }
}

export function useMaintenancePage(
  page?: number,
  pageSize?: number,
  startDate?: string,
  endDate?: string,
  isAllData?: boolean
) {

  return useQuery<ApiResponse, Error>({
    queryKey: ["maintenancePage", page, pageSize, startDate, endDate, isAllData],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page || 1),
        page_size: String(pageSize || 10),
      });

      if (!isAllData) {
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
      }

      if (isAllData) {
        params.append("all", "true");
      }

      const url = `/site-event?${params.toString()}`;

      const response = await apiClient.get<ApiResponse>(url);
      return response;
    },
    // enabled: !!startDate && !!endDate, // (keep commented if not needed)
  });
}